import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Users, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import {
    Pagination, PaginationContent, PaginationItem, PaginationLink,
    PaginationNext, PaginationPrevious, PaginationEllipsis,
} from "@/components/ui/pagination";
import { SessionCard } from "@/components/SessionCard";
import { EmptyState } from "@/components/EmptyState";
import { AvatarCardSkeleton } from "@/components/Skeleton";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const PAGE_SIZE = 12;

const SORT_OPTIONS = [
    { value: "recent", label: "Most recent" },
    { value: "oldest", label: "Oldest first" },
    { value: "score_desc", label: "Highest score" },
    { value: "score_asc", label: "Lowest score" },
];

export default function AllSessionsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    useDocumentTitle("All sessions");

    const page = parseInt(searchParams.get("page") || "1", 10);
    const sort = searchParams.get("sort") || "recent";
    const therapyFilter = searchParams.get("therapy_type") || "all";
    const minScore = parseInt(searchParams.get("min_score") || "0", 10);
    const maxScore = parseInt(searchParams.get("max_score") || "100", 10);

    const [scoreLocal, setScoreLocal] = useState([minScore, maxScore]);
    const [data, setData] = useState(null);
    const [therapyTypes, setTherapyTypes] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/therapy-types").then((r) => setTherapyTypes(r.data)).catch(() => setTherapyTypes([]));
    }, []);

    useEffect(() => {
        setScoreLocal([minScore, maxScore]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams.get("min_score"), searchParams.get("max_score")]);

    const fetchFeed = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, page_size: PAGE_SIZE, sort };
            if (therapyFilter !== "all") params.therapy_type = therapyFilter;
            if (minScore > 0) params.min_score = minScore;
            if (maxScore < 100) params.max_score = maxScore;
            const res = await api.get("/sessions/feed", { params });
            setData(res.data);
        } catch (err) {
            toast.error("Could not load the peer feed");
            setData({ items: [], total: 0, total_pages: 0, page: 1, page_size: PAGE_SIZE });
        } finally {
            setLoading(false);
        }
    }, [page, sort, therapyFilter, minScore, maxScore]);

    useEffect(() => { fetchFeed(); }, [fetchFeed]);

    const updateParam = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value == null || value === "all" || value === "") next.delete(key);
        else next.set(key, String(value));
        if (key !== "page") next.delete("page");
        setSearchParams(next, { replace: true });
    };

    const commitScoreRange = (range) => {
        const next = new URLSearchParams(searchParams);
        if (range[0] > 0) next.set("min_score", String(range[0]));
        else next.delete("min_score");
        if (range[1] < 100) next.set("max_score", String(range[1]));
        else next.delete("max_score");
        next.delete("page");
        setSearchParams(next, { replace: true });
    };

    const hasActiveFilters = useMemo(
        () => therapyFilter !== "all" || minScore > 0 || maxScore < 100 || sort !== "recent",
        [therapyFilter, minScore, maxScore, sort]
    );

    const clearFilters = () => setSearchParams({}, { replace: true });

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in-up" data-testid="all-sessions-page">
                <div>
                    <p className="text-sm font-medium text-primary">Community</p>
                    <h1 className="mt-1 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                        All reviewed sessions
                    </h1>
                    <p className="mt-2 max-w-xl text-sm text-muted-foreground">
                        Read-only browse of every reviewed session across Happy Lab. Useful when you want to see how peers handled the same case.
                    </p>
                </div>

                {/* Sticky filter bar */}
                <Card
                    className="sticky top-16 z-10 border-border/80 bg-background/95 shadow-sm backdrop-blur"
                    data-testid="all-sessions-filter-bar"
                >
                    <CardContent className="grid gap-4 p-4 md:grid-cols-[1fr_1fr_1fr_auto] md:items-end">
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Therapy type</Label>
                            <Select value={therapyFilter} onValueChange={(v) => updateParam("therapy_type", v)}>
                                <SelectTrigger data-testid="all-sessions-filter-therapy">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Any therapy</SelectItem>
                                    {therapyTypes.map((t) => (
                                        <SelectItem key={t.slug} value={t.slug} data-testid={`all-sessions-filter-therapy-${t.slug}`}>
                                            {t.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5 md:col-span-2">
                            <div className="flex items-center justify-between">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Score range</Label>
                                <span className="text-xs tabular-nums text-muted-foreground" data-testid="all-sessions-score-range-label">
                                    {scoreLocal[0]} – {scoreLocal[1]}
                                </span>
                            </div>
                            <Slider
                                value={scoreLocal}
                                onValueChange={setScoreLocal}
                                onValueCommit={commitScoreRange}
                                min={0}
                                max={100}
                                step={5}
                                data-testid="all-sessions-filter-score-slider"
                                className="py-2"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Sort</Label>
                            <Select value={sort} onValueChange={(v) => updateParam("sort", v)}>
                                <SelectTrigger data-testid="all-sessions-filter-sort">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {SORT_OPTIONS.map((o) => (
                                        <SelectItem key={o.value} value={o.value} data-testid={`all-sessions-filter-sort-${o.value}`}>
                                            {o.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {hasActiveFilters && (
                            <div className="md:col-span-4">
                                <Button onClick={clearFilters} variant="ghost" size="sm" className="h-8 text-xs" data-testid="all-sessions-filter-clear">
                                    <RotateCcw className="h-3 w-3" />
                                    Clear filters
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {data && (
                    <p className="text-sm text-muted-foreground" data-testid="all-sessions-result-count">
                        {data.total === 0
                            ? "No reviewed sessions match these filters yet."
                            : `${data.total} reviewed session${data.total === 1 ? "" : "s"} across the platform`}
                    </p>
                )}

                {loading && !data ? (
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {Array.from({ length: 6 }).map((_, i) => <AvatarCardSkeleton key={i} />)}
                    </div>
                ) : data?.items.length === 0 ? (
                    <EmptyState
                        icon={Users}
                        title={hasActiveFilters ? "Nothing here yet" : "No reviewed sessions yet"}
                        description={
                            hasActiveFilters
                                ? "Try widening the filters."
                                : "Once any student finishes a reviewed session, it'll appear here."
                        }
                        action={
                            hasActiveFilters && (
                                <Button variant="secondary" size="sm" onClick={clearFilters} data-testid="all-sessions-empty-clear">
                                    Clear filters
                                </Button>
                            )
                        }
                    />
                ) : (
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="all-sessions-grid">
                        {data?.items.map((s) => (
                            <SessionCard key={s.id} session={s} isPublic showCounsellor />
                        ))}
                    </div>
                )}

                {data && data.total_pages > 1 && (
                    <Paginator
                        page={data.page}
                        totalPages={data.total_pages}
                        onChange={(p) => updateParam("page", p)}
                    />
                )}
            </div>
        </Layout>
    );
}

const Paginator = ({ page, totalPages, onChange }) => {
    const pages = useMemo(() => {
        const set = new Set([1, totalPages, page - 1, page, page + 1]);
        return Array.from(set)
            .filter((p) => p >= 1 && p <= totalPages)
            .sort((a, b) => a - b);
    }, [page, totalPages]);

    return (
        <Pagination className="pt-4" data-testid="all-sessions-pagination">
            <PaginationContent>
                <PaginationItem>
                    <PaginationPrevious
                        onClick={(e) => { e.preventDefault(); if (page > 1) onChange(page - 1); }}
                        href="#"
                        aria-disabled={page <= 1}
                        className={page <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        data-testid="pagination-prev"
                    />
                </PaginationItem>
                {pages.map((p, i) => {
                    const showEllipsis = i > 0 && p - pages[i - 1] > 1;
                    return (
                        <React.Fragment key={p}>
                            {showEllipsis && (
                                <PaginationItem>
                                    <PaginationEllipsis />
                                </PaginationItem>
                            )}
                            <PaginationItem>
                                <PaginationLink
                                    isActive={p === page}
                                    onClick={(e) => { e.preventDefault(); onChange(p); }}
                                    href="#"
                                    className="cursor-pointer"
                                    data-testid={`pagination-page-${p}`}
                                >
                                    {p}
                                </PaginationLink>
                            </PaginationItem>
                        </React.Fragment>
                    );
                })}
                <PaginationItem>
                    <PaginationNext
                        onClick={(e) => { e.preventDefault(); if (page < totalPages) onChange(page + 1); }}
                        href="#"
                        aria-disabled={page >= totalPages}
                        className={page >= totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        data-testid="pagination-next"
                    />
                </PaginationItem>
            </PaginationContent>
        </Pagination>
    );
};
